import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { Office, useGetCompanyOffices } from '~/data/companyOffices';
import { fetchEmployee } from '~/data/employee';
import { useEmployee } from '~/data/employee/useEmployee';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { ContactTabSectionHeadingProps } from '~/scenes/PeopleModule/components/Tabs/ContactTab/components/ContactTabSectionHeading';
import { AppDispatch } from '~/store';
import { isValidOptionalPhoneNumber } from '~/utils/helper';

import { EmpOffContactType, putEmpOffContactData } from './data';

export type IFormInput = {
  fEmpEmailWork: string;
  fEmpOfficeFloor?: string;
  fEmpOfficeLoc: number;
  fEmpOfficeRoom?: string;
  fEmpPhoneMob?: string;
  fEmpPhoneWork?: string;
};

export type UseWorkContractOutput = {
  form: UseFormReturn<IFormInput>;
  actions: Pick<
    ContactTabSectionHeadingProps,
    'onEdit' | 'onSave' | 'onCancel'
  >;
  readMode: boolean;
  empData: EmpOffContactType;
  companyOffice: Office[];
  isLoading: boolean;
};

export const useWorkContact = (): UseWorkContractOutput => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch: AppDispatch = useDispatch();

  const {
    employee: { empWorkContact: empData, empBasicData },
    isLoading: isLoadingEmployee,
  } = useEmployee();

  const { data: companyOffice, isLoading: isLoadingCompanyOffices } =
    useGetCompanyOffices({ revalidateOnFocus: false });

  const isLoading = isLoadingEmployee || isLoadingCompanyOffices;

  const [readMode, setReadMode] = useState(true);

  const validationSchema = useMemo(() => {
    const yupOptionalPhoneNumber = yup
      .string()
      .test(
        'phoneNumberValidation',
        t('phoneInvalid'),
        isValidOptionalPhoneNumber,
      );

    return yup.object().shape<IFormInput>({
      fEmpPhoneWork: yupOptionalPhoneNumber,
      fEmpPhoneMob: yupOptionalPhoneNumber,
      fEmpEmailWork: yup
        .string()
        .email(t('laValidEmail'))
        .required(t('laFillManFields')),
      fEmpOfficeLoc: yup.number().required(t('laFillManFields')),
      fEmpOfficeFloor: yup.string(),
      fEmpOfficeRoom: yup.string(),
    });
  }, [t]);
  const resolver = useYupValidationResolver(validationSchema);

  const form = useForm<IFormInput>({
    resolver,
    defaultValues: empData,
  });
  const { handleSubmit, reset } = form;

  const handleFormReset = () => {
    reset();
    setReadMode(true);
  };

  const handleFormSubmit = useMemo(() => {
    return handleSubmit((data) => {
      const json: IFormInput = {
        fEmpEmailWork: data.fEmpEmailWork,
        fEmpOfficeFloor: data.fEmpOfficeFloor,
        fEmpOfficeLoc: data.fEmpOfficeLoc,
        fEmpOfficeRoom: data.fEmpOfficeRoom,
        fEmpPhoneMob: data.fEmpPhoneMob,
        fEmpPhoneWork: data.fEmpPhoneWork,
      };
      (async () => {
        try {
          await putEmpOffContactData(json, empBasicData.fEmpId);
          enqueueSnackbar(t('change_saved'), {
            variant: 'success',
          });
          dispatch(fetchEmployee({ empId: empBasicData.fEmpId }));
          setReadMode(true);
        } catch (error) {
          enqueueSnackbar(t('errorMsg'), {
            variant: 'error',
          });
        }
      })();
    });
  }, [dispatch, empBasicData.fEmpId, enqueueSnackbar, handleSubmit, t]);

  const onSubmit = () => {
    handleFormSubmit();
  };

  return {
    form,
    actions: {
      onSave: onSubmit,
      onCancel: handleFormReset,
      onEdit: () => setReadMode(false),
    },
    companyOffice,
    readMode,
    empData,
    isLoading,
  };
};
